// @flow
import * as React from "react";
import styled, { css } from "styled-components";
import Gettext from "./common/Gettext";
import Auth from "./Auth/Auth";
import LoggedIn from "./Auth/Tabs/LoggedIn";
import Spinner from "./Spinners/SimpleSpinner";
import Tournament from "./Tournaments/Api";
import itunes from "./../images/web/itunes-btn.png";
import itunes2x from "./../images/web/itunes-btn@2.png";
import itunes3x from "./../images/web/itunes-btn@3.png";
import googlePlay from "./../images/web/google-play-btn.png";
import googlePlay2x from "./../images/web/google-play-btn@2.png";
import googlePlay3x from "./../images/web/google-play-btn@3.png";
import { gaReducer } from "./GA";
import withErrorCatch from "./withErrorCatch";

import type { Location, RouterHistory } from "react-router-dom";
import type { Profile } from "./../redux/CommonTypes";

const isIronAuth =
  process.env.REACT_APP_DEPLOY_TARGET === "irontrade" ||
  process.env.REACT_APP_DEPLOY_TARGET === "eztrader";

const SpinnerContainer = styled.div`
  position: relative;
  -webkit-display: flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-align-self: center;
  align-self: center;
  -webkit-justify-content: center;
  justify-content: center;
  float: right;
  width: 450px;
  height: 420px;
  margin: 0 auto;
  font-size: 36px;

  @media (max-width: 900px) {
    float: none;
    width: 325px;
  }

  @media (max-width: 360px) {
    width: 300px;
  }
`;

const AuthWrapper = styled.div`
  position: relative;
  display: ${isIronAuth ? "block" : "inline-block"};
  vertical-align: top;
  float: ${(props) => (props.isAppLogin || isIronAuth ? "none" : "right")};

  ${isIronAuth &&
    css`
      margin-bottom: 50px;
    `};

  @media (max-width: 900px) {
    display: block;
    float: none;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 450px;
  height: ${isIronAuth ? "auto" : "420px"};
  margin: 0 auto;
  margin-bottom: ${(props) => (props.bigBottomMargin ? 100 : 0)}px;
  text-align: center;

  @media (max-width: 900px) {
    height: auto;
  }

  @media (max-width: 480px) {
    width: 340px;
  }

  @media (max-width: 360px) {
    width: 300px;
  }
`;

const LoggedInWrapper = styled(Wrapper)`
  display: table;
  width: 335px;
  padding-top: 52px;

  @media (max-width: 900px) {
    height: auto;
    padding: 20px 0;
    padding-top: 32px;
  }

  @media (max-width: 359px) {
    width: 325px;
  }
`;

const LoginContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding-top: 52px;

  @media (max-width: 900px) {
    padding-top: 32px;
  }
`;

const SocialContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding-top: 20px;

  @media (max-width: 900px) {
    padding-top: 10px;
  }
`;

const LoggedInContainer = styled.div`
  display: table-cell;
  vertical-align: middle;
  width: 100%;
  padding: 0;
  color: #a79eaa;
  text-align: left;

  @media (max-width: 900px) {
    display: block;
    padding-left: 0;
  }
`;

const Header = styled.div`
  width: 100%;
  padding-bottom: 10px;
  background-image: ${({ theme }) =>
    process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
      ? "linear-gradient(to bottom, #1B546A 0%, #399177 100%)"
      : `url(${theme.indexHeaderBackground})`};
  background-repeat: no-repeat;
  background-position: 50% 0;
  background-size: cover;
  background-color: ${(props) => props.theme.main};

  @media (max-width: 900px) {
    padding-bottom: 20px;
  }

  ${(props) =>
    props.isMainPage &&
    css`
      margin-top: -75px;
      padding-top: 75px;

      @media (max-width: 959px) {
        margin-top: -96px;
        padding-top: 96px;
      }
    `};

  ${(props) =>
    props.isAppLogin &&
    css`
      position: fixed;
      top: 48px;
      left: 0;
      right: 0;
      bottom: 0;
      overflow: auto;

      @media (min-width: 960px) {
        top: 75px;
      }
    `};
`;

const HeaderWrapper = styled.div`
  display: table;
  width: 100%;
  max-width: 960px;
  min-width: 320px;
  margin: 0 auto;
  padding: 0 10px;
  font-size: 0;
  line-height: 0;
  text-align: justify;
  clear: both;

  ${(props) =>
    props.isAppLogin &&
    css`
      text-align: center;
    `};

  :after {
    display: inline-block;
    width: 100%;
    content: "";
  }
`;

const BrandContainer = styled.div`
  float: left;
  display: table;
  vertical-align: top;
  width: 390px;
  height: 420px;
  margin-top: 0;
  font-size: 16px;
  line-height: 1;

  @media (min-width: 960px) {
    margin-left: 60px;
  }

  @media (max-width: 900px) {
    float: none;
    display: block;
    height: auto;
    margin-top: 20px;
    margin-left: auto;
    margin-right: auto;
    width: 325px;
  }

  @media (max-width: 480px) {
    width: 325px;
    margin-top: 10px;
  }

  @media (max-width: 360px) {
    width: 300px;
  }
`;

const BrandContainerInner = styled.div`
  display: table-cell;
  vertical-align: middle;

  @media (max-width: 480px) {
    display: block;
  }
`;

const BrandLogo = styled.div`
  width: 108px;
  height: 82px;
  margin: 0 auto 14px;
  background: url(${(props) => props.theme.indexLogo}) no-repeat 50% 50%;
  background-size: cover;

  @media (max-width: 900px) {
    width: 109px;
    height: 83px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 44px;
  }

  ${(props) =>
    props.thProject &&
    css`
      width: 130px;
      height: 82px;

      @media (max-width: 900px) {
        width: 130px;
        height: 82px;
      }

      @media (max-width: 480px) {
        width: 70px;
        height: 44px;
      }
    `};
`;

const BrandLogoText = styled.span`
  display: block;
  width: 202px;
  height: 26px;
  margin: 0 auto 15px;
  background: url(${(props) => props.theme.indexLogoText}) no-repeat 50% 50%;
  background-size: cover;

  @media (max-width: 900px) {
    width: 193px;
    height: 25px;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    width: 165px;
    height: 22px;
    margin-bottom: 15px;
  }

  ${(props) =>
    props.thProject &&
    css`
      width: 161px;
      height: 30px;

      @media (max-width: 900px) {
        width: 161px;
        height: 30px;
      }

      @media (max-width: 480px) {
        width: 161px;
        height: 30px;
        margin-bottom: 5px;
      }
    `};
`;

const BrandTitle = styled.div`
  margin-bottom: 26px;
  font-size: 24px;
  text-align: center;
  color: #feac00;

  @media (max-width: 900px) {
    margin-bottom: 10px;
    font-size: 24px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const BrandText = styled.div`
  font-size: ${({ theme }) => (theme.locale === "ar" ? "20px" : "16px")};
  line-height: 1.2;
  text-align: center;
  color: #fdfdfd;
`;

const AppLinks = styled.div`
  margin-top: 20px;
  overflow: hidden;
  font-size: 0;
  line-height: 0;
`;

const AppLink = styled.a`
  float: right;
  display: block;
  width: 180px;
  height: 49px;
  margin: 0 auto;
  font-size: 0;
  line-height: 0;
  text-decoration: none;

  @media (max-width: 900px) {
    width: 140px;
    height: 39px;
  }

  :first-child {
    float: left;
  }

  > img {
    display: block;
    max-width: 100%;
  }
`;

// const AppLinkOnly = AppLink.extend`float: none !important;`;

type Props = {
  isAppLogin?: boolean,
  ref_user?: string,
  geo: string,
  isVIP: boolean,
  profileStatus: string,
  profile: Profile,
  location: Location,
  history: RouterHistory,
  currencySettings: Object,
  authTab: string,
  isHelpdesk: boolean,
};

export function Authentication(props: Props) {
  const query = props.query;
  window.isFullAndroidApp = /wvbo_full/g.test(window.navigator.userAgent);

  let chucknorris = "";
  if (window.location.search.indexOf("chucknorris=yes") > 0) {
    chucknorris = "?chuck-norris=yes";
  } else if (window.location.search.indexOf("chucknorris=no") > 0) {
    chucknorris = "?chuck-norris=no";
  }

  const restrictedEntity = props.api.readEntity(
    `/api/is-restricted${chucknorris}`
  );
  const isRestricted =
    restrictedEntity.value && restrictedEntity.value.restricted;

  const loggedin = (
    <LoggedInWrapper>
      <LoggedInContainer>
        <LoggedIn
          isVIP={props.isVIP}
          geo={props.geo}
          currencySettings={props.currencySettings}
          profile={props.profile}
        />
      </LoggedInContainer>
    </LoggedInWrapper>
  );

  if (props.profileStatus === "AVAILABLE") {
    // return loggedin;
  }

  switch (props.authTab) {
    case "loggedin":
      return loggedin;
    case "social_register":
      return (
        <Wrapper isAppLogin={props.isAppLogin} bigBottomMargin={isRestricted}>
          <SocialContainer>
            <Auth
              geo={props.geo}
              isVIP={props.isVIP}
              activeTab="social_register"
              showSocial={false}
              profile={props.profile}
              isAppLogin={props.isAppLogin}
            />
          </SocialContainer>
        </Wrapper>
      );
    case "social_password":
      return (
        <Wrapper isAppLogin={props.isAppLogin} bigBottomMargin={isRestricted}>
          <LoginContainer>
            <Auth
              geo={props.geo}
              isVIP={props.isVIP}
              activeTab="social_password"
              email={query.email}
              query={query}
              location={props.location}
              history={props.history}
              ref_user={props.ref_user}
              isDarkPage
            />
          </LoginContainer>
        </Wrapper>
      );
    case "register":
    case "login":
    case "password_restore":
      return (
        <Wrapper isAppLogin={props.isAppLogin} bigBottomMargin={isRestricted}>
          <LoginContainer>
            <Auth
              geo={props.geo}
              isVIP={props.isVIP}
              activeTab={props.authTab}
              showSocial={!window.isFullAndroidApp}
              ref_user={props.ref_user}
              isDarkPage
            />
          </LoginContainer>
        </Wrapper>
      );
    default:
      return (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      );
  }
}

function HeaderMain(props: Props) {
  return (
    <Header
      isAppLogin={props.isAppLogin}
      isMainPage={props.location && props.location.pathname === "/"}
    >
      <HeaderWrapper isAppLogin={props.isAppLogin}>
        {!props.isAppLogin &&
          process.env.REACT_APP_DEPLOY_TARGET === "siamoption" && (
            <BrandContainer>
              <BrandContainerInner>
                <BrandLogo
                  thProject={
                    process.env.REACT_APP_DEPLOY_TARGET === "siamoption"
                  }
                />
                <BrandLogoText
                  thProject={
                    process.env.REACT_APP_DEPLOY_TARGET === "siamoption"
                  }
                />
                <BrandTitle>
                  <Gettext>Binary Options Broker</Gettext>
                </BrandTitle>
                <BrandText>
                  <Gettext>
                    Predict price movements and get profit on ups and downs
                  </Gettext>
                </BrandText>
                {process.env.REACT_APP_DEPLOY_TARGET === "siamoption" && (
                  <AppLinks>
                    <AppLink
                      href={
                        props.isHelpdesk
                          ? "https://app.appsflyer.com/id1169036197?pid=helpdesk&media_source=helpdesk&campaign=appstore_button"
                          : "https://app.appsflyer.com/id1169036197?pid=main_page&media_source=main_page&campaign=appstore_button"
                      }
                      onClick={(e) =>
                        gaReducer("CLICKBTN_DOWNLOADAPP_MAINPAGE", {
                          appSrc: e.currentTarget.href,
                        })
                      }
                    >
                      <img
                        src={itunes}
                        srcSet={itunes2x + " 2x, " + itunes3x + " 3x"}
                        alt="Itunes"
                      />
                    </AppLink>
                    <AppLink
                      href={
                        props.isHelpdesk
                          ? "https://play.google.com/store/apps/details?id=com.full.iron&referrer=sub1%3Dmainpage"
                          : "https://app.appsflyer.com/com.siamtrade.mobile?pid=main_page&media_source=main_page&c=googleplay_button"
                      }
                      onClick={(e) =>
                        gaReducer("CLICKBTN_DOWNLOADAPP_MAINPAGE", {
                          appSrc: e.currentTarget.href,
                        })
                      }
                    >
                      <img
                        src={googlePlay}
                        srcSet={googlePlay2x + " 2x, " + googlePlay3x + " 3x"}
                        alt="Google Play"
                      />
                    </AppLink>
                  </AppLinks>
                )}
              </BrandContainerInner>
            </BrandContainer>
          )}

        <AuthWrapper>
          <Tournament.Consumer>
            {(api) => <Authentication {...props} api={api} />}
          </Tournament.Consumer>
        </AuthWrapper>
      </HeaderWrapper>
    </Header>
  );
}

export default withErrorCatch(HeaderMain);



// WEBPACK FOOTER //
// ./src/components/HeaderMain.js